/**
 * @file Notifications empilees en haut (max 3) : evenements Toast du core, erreurs de commandes, deblocages, nouvelles armes.
 */
import type { ToastKind } from '@core/index';
import { gradeIndex } from '@shared/enums';
import { formatBig } from '@shared/format';
import { Component } from '../core/Component';
import { h } from '../core/dom';
import { toastSink } from '../core/feedback';
import type { ToastSink } from '../core/feedback';
import type { UiContext } from '../UiContext';

const MAX_VISIBLE = 3;
const LIFETIME_S = 2.2;
/** Un meme texte n'est pas empile deux fois dans cette fenetre (la commande et l'evenement du core se doublonnent parfois). */
const DEDUPE_S = 0.6;

interface Live {
  el: HTMLElement;
  text: string;
  age: number;
}

export class Toasts extends Component implements ToastSink {
  private live: Live[] = [];

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'toasts');
  }

  protected build(): void {
    toastSink.current = this;
    this.addDisposer(() => {
      if (toastSink.current === this) toastSink.current = null;
    });
    const t = this.ctx.i18n;

    // Le core envoie parfois un identifiant de fonctionnalite comme `name` : on le traduit ici (il ne connait pas les textes).
    const localizeFeature = (n: string): string => {
      for (const k of [n, `feature.${n}`]) {
        const v = t.tDyn(k);
        if (v !== k) return v;
      }
      return n;
    };
    this.listen('Toast', (e) => {
      const params = e.params ? { ...e.params } : undefined;
      if (e.key === 'toast.unlocked' && params && typeof params.name === 'string') params.name = localizeFeature(params.name);
      this.show(t.tDyn(e.key, params), e.kind);
    });
    this.listen('WeaponDrawn', (e) => {
      // Un seul toast pour un tirage multiple : la meilleure arme.
      let best: string | null = null;
      let bestIdx = -1;
      for (const id of e.defIds) {
        const idx = gradeIndex(this.ctx.index.weapon(id).grade);
        if (idx > bestIdx) {
          bestIdx = idx;
          best = id;
        }
      }
      if (best) this.show(t.t('toast.newWeapon', { name: t.name(this.ctx.index.weapon(best).name) }), 'reward');
    });
    this.listen('WeaponsMerged', (e) => {
      // fusions auto aussi annoncees (avec le grade obtenu) : la cascade se fait pas a pas et chaque palier doit se lire
      const def = this.ctx.index.weapon(e.resultDefId);
      this.show(t.t(e.auto ? 'toast.mergedAuto' : 'toast.merged', { name: t.name(def.name), grade: def.grade }), 'success');
    });
    this.listen('FeatureUnlocked', (e) => this.show(t.t('toast.unlocked', { name: localizeFeature(e.feature) }), 'success'));
    this.listen('MissionCompleted', () => this.show(t.t('toast.missionDone'), 'success'));
    this.listen('RebirthDone', (e) => this.show(t.t('toast.rebirth', { rubies: formatBig(e.rubies) }), 'reward'));
  }

  show(text: string, kind: ToastKind = 'info'): void {
    if (this.live.some((l) => l.text === text && l.age < DEDUPE_S)) return;
    const el = h('div', { class: `toast ${kind}`, role: 'status' }, text);
    this.el.append(el);
    this.live.push({ el, text, age: 0 });
    while (this.live.length > MAX_VISIBLE) this.live.shift()?.el.remove();
  }

  override update(dt: number): void {
    for (const l of this.live) l.age += dt;
    this.live = this.live.filter((l) => {
      if (l.age < LIFETIME_S) return true;
      l.el.remove();
      return false;
    });
  }
}
